import i from 'i18next'
import { currency } from 'lib/currency'
import {
	Cart as CartType,
	GIFT_1_THRESHOLD,
	GIFT_2_THRESHOLD,
	getSubtotalOfItemsEligibleForGift,
} from 'lib/hooks/use-cart'
import { mobileMenuMaxWidth } from 'constants/styles'
import { motion } from 'framer-motion'
import {
	BENELUX,
	BENELUX_FREE,
	BENELUX_SHIPPING,
	EUROPE,
	EUROPE_FREE,
	EUROPE_SHIPPING,
} from '../Cart'
import { Suggestions } from './Suggestions'
import { MouseEventHandler } from 'react'
import { cvgTrack, parseCart } from 'components/analytics/Converge'

type Props = {
	cart: CartType
	zone: typeof BENELUX | typeof EUROPE
}

export const Footer: React.FC<Props> = ({ cart, zone }) => {
	// Calculate shipping costs
	let shippingCost = zone === EUROPE ? EUROPE_SHIPPING : BENELUX_SHIPPING
	const shippingFree = zone === EUROPE ? EUROPE_FREE : BENELUX_FREE
	const subtotal = parseFloat( cart?.lineItemsSubtotalPrice?.amount )
	const subtotalWithoutGiftcards = getSubtotalOfItemsEligibleForGift( cart?.lineItems ?? [] )
	if (
		( zone === BENELUX && subtotalWithoutGiftcards >= BENELUX_FREE ) ||
		( zone === EUROPE && subtotalWithoutGiftcards >= EUROPE_FREE )
	) {
		shippingCost = 0
	}

	const freeShippingPercent = Math.min( 100, ( subtotalWithoutGiftcards / shippingFree ) * 100 )
	const percent =
		( freeShippingPercent +
			( cart?.freeGift?.percent || 0 ) +
			( cart?.freeGift2?.percent || 0 ) ) %
		100
	const remaining =
		freeShippingPercent < 100
			? shippingFree - subtotalWithoutGiftcards
			: cart?.freeGift?.percent < 100
				? cart?.freeGift?.remaining
				: cart?.freeGift2?.remaining

	const handleClickCheckout: MouseEventHandler<HTMLAnchorElement> = ( e ) => {
		e.currentTarget.classList.add( 'disabled' )

		if ( window.dataLayer ) {
			window.dataLayer.push({ ecommerce: null })
			window.dataLayer.push({
				event: 'checkout',
				ecommerce: {
					products: cart.lineItems.map( ( item ) => ({
						name: item.title,
						id: item.variant.product.id.substring(
							item.variant.product.id.lastIndexOf( '/' ) + 1
						),
						price: item.variant.priceV2,
						variant: item.variant.selectedOptions
							.map( ( o ) => o.value )
							.join( ' / ' ),
						quantity: item.quantity,
					}) ),
				},
			})
		}
		const cartId = cart.id.split( '/' ).pop()
		cvgTrack({
			eventName: 'Clicked Checkout',
			properties: parseCart( cart ),
			aliases: cartId ? [ `urn:shopify:cart_token:${cartId}` ] : [],
		})
	}

	return (
		<footer>
			{!!remaining && (
				<div className="complete-cart">
					<div className="row progress">
						<div className="text">
							{freeShippingPercent < 100
								? i.t( 'cart.progress.shipping', {
									amount: currency.format( BENELUX_FREE ),
								})
								: cart?.freeGift?.remaining
									? i.t( 'cart.progress.gift', {
										amount: currency.format( GIFT_1_THRESHOLD ),
									})
									: cart?.freeGift2?.remaining
										? i.t( 'cart.progress.gift2', {
											amount: currency.format( GIFT_2_THRESHOLD ),
										})
										: null}
						</div>
						<div className="text-short">
							{freeShippingPercent < 100
								? i.t( 'cart.progress.shipping.short', {
									amount: currency.format( BENELUX_FREE ),
								})
								: cart?.freeGift?.remaining
									? i.t( 'cart.progress.gift.short', {
										amount: currency.format( GIFT_1_THRESHOLD ),
									})
									: cart?.freeGift2?.remaining
										? i.t( 'cart.progress.gift2.short', {
											amount: currency.format( GIFT_2_THRESHOLD ),
										})
										: null}
						</div>
						<div>
							{i.t( 'cart.progress.amount', {
								amount: currency.format( remaining ),
							})}
						</div>
					</div>
					<div className="progress-bar">
						<motion.div
							className="fill"
							animate={{ width: `${percent}%` }}
							transition={{
								width: {
									type: 'spring',
									duration: 1,
									bounce: 0.15,
								},
							}}
						/>
					</div>
				</div>
			)}
			{cart && <Suggestions cart={cart} />}
			<div className="row small">
				<div>
					{shippingCost > 0
						? i.t( 'cart.shipping.estimated' )
						: i.t( 'cart.shipping' )}
				</div>
				<div>
					{shippingCost === 0
						? i.t( 'cart.free' )
						: currency.format( shippingCost )}
				</div>
			</div>
			{cart?.count ? (
				<a
					href={cart?.checkoutUrl}
					className="button"
					onClick={handleClickCheckout}
				>
					<span className="mobile-only">{i.t( 'cart.button.small' )}</span>
					<span className="desktop-only">{i.t( 'cart.button' )}</span>
					{subtotal > 0 && (
						<>
							&nbsp;—&nbsp;
							{currency.format( subtotal )}
						</>
					)}
					<svg viewBox="25 25 50 50" width="35" height="35">
						<circle
							cx="50"
							cy="50"
							r="20"
							fill="none"
							stroke="#8a8f95"
							strokeWidth="4"
						/>
					</svg>
				</a>
			) : (
				<div className="button disabled">
					<span className="mobile-only">{i.t( 'cart.button.small' )}</span>
					<span className="desktop-only">{i.t( 'cart.button' )}</span>
				</div>
			)}

			<style jsx>{`
				@media (max-width: 500px) {
					.desktop-only {
						display: none;
					}
				}
				@media (min-width: 501px) {
					.mobile-only {
						display: none;
					}
				}

				footer {
					align-self: stretch;
					display: grid;
					grid-template-rows: 1fr;
					grid-gap: 16px;
					padding: 30px;
					padding-top: 20px;
					background-color: #f7f7f7;
					font-size: 24px;
					line-height: 60px;
					text-transform: lowercase;
				}
				@media (max-height: 750px) {
					footer {
						padding-top: 10px;
					}
				}
				.row {
					display: flex;
					justify-content: space-between;
				}
				footer :global(.row.small) {
					font-size: 15px;
					line-height: 20px;
					color: #8a8f95;
				}
				.complete-cart :global(+ .row.small) {
					display: none;
				}
				.row.progress {
					font-size: 16px;
					line-height: 2.4;
				}
				@media ( max-width: 500px ) {
					.row.progress {
						font-size: 14px;
					}
				}
				.row.progress > div:last-child {
					color: #8a8f95;
				}
				@media (max-width: 400px) {
					.progress .text {
						display: none;
					}
				}
				@media (min-width: 401px) {
					.progress .text-short {
						display: none;
					}
				}
				.progress-bar {
					position: relative;
					width: 100%;
					height: 2px;
					margin-bottom: 1px;
					background-color: #d8dde3;
				}
				.progress-bar :global(.fill) {
					position: absolute;
					left: 0;
					top: 0;
					height: 100%;
					background-color: black;
				}
				.button {
					white-space: nowrap;
					transition: color 0.3s, background-color 0.6s;
				}
				.button.disabled {
					background-color: #eceaea;
					color: transparent;
				}
				.button.disabled svg {
					opacity: 1;
				}
				div.button.disabled {
					color: #8a8f95;
				}

				.button svg {
					position: absolute;
					left: 50%;
					transform: translateX(-50%);
					animation: rotate 2s linear infinite;
					transform-origin: center center;
					margin: auto;
					opacity: 0;
					transition: opacity 0.2s;
				}
				.button svg circle {
					stroke-dasharray: 150, 200;
					stroke-dashoffset: -10;
					animation: dash 1.5s ease-in-out infinite;
				}
				@keyframes rotate {
					100% {
						-webkit-transform: translateX(-50%) rotate(360deg);
						transform: translateX(-50%) rotate(360deg);
					}
				}
				@keyframes dash {
					0% {
						stroke-dasharray: 1, 200;
						stroke-dashoffset: 0;
					}
					50% {
						stroke-dasharray: 89, 200;
						stroke-dashoffset: -35;
					}
					100% {
						stroke-dasharray: 89, 200;
						stroke-dashoffset: -124;
					}
				}

				@media (max-width: ${mobileMenuMaxWidth}px) {
					.button {
						height: 60px;
					}
				}

				@media (max-height: 600px) {
					footer {
						padding: 15px 30px 20px 30px;
					}
					.button {
						height: 50px;
					}
				}
			`}</style>
		</footer>
	)
}
